import { useCallback, useEffect, useState, useMemo } from 'react';

import queryString from 'query-string';
import { clone, difference } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { PAGE_TYPE_ACTIVITY, PAGE_TYPE_AREA, PAGE_TYPE_PROPERTY } from 'constants/index';
import { useAccountPath } from 'hooks/useAccountPath';
import useQueryParams from 'hooks/useQueryParams';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { useTryToRemindCheckPrimaryImage } from 'utils/itineraries/functions';

import { EDetailsMode } from '../Itinerary/Details/types';
import { SEARCH_FILTER_SHOW_STATUS } from './constants';

export function useGlobalSearch() {
  const { q, setQ } = useQueryParams();
  const [searchValue, setSearchValue] = useState(q);

  const handleChangeSearchValue = useCallback((newValue) => {
    setSearchValue(newValue || undefined);
  }, []);

  const handleClearSearchValue = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleSubmit = useCallback(() => {
    setQ(searchValue || undefined);
  }, [setQ, searchValue]);

  return {
    searchValue,
    handleChangeSearchValue,
    handleClearSearchValue,
    handleSubmit,
  };
}

export function useTableCollapsing({ content, collapsedTablesRoute, expandTableRoute }) {
  const getPath = useAccountPath();
  const history = useHistory();
  const { queryParams } = useQueryParams();

  return useCallback(
    (contentName, tableQueryParams = {}) => {
      const collapsedQuery = queryString.stringify(
        {
          q: queryParams.q,
        },
        { skipNull: true, skipEmptyString: true },
      );
      const collapsedQs = collapsedQuery ? `?${collapsedQuery}` : '';

      if (content === contentName) {
        history.push(getPath(collapsedTablesRoute) + collapsedQs);
        return;
      }

      const tableQuery = queryString.stringify(
        {
          q: queryParams.q,
          ...tableQueryParams,
        },
        { skipNull: true, skipEmptyString: true },
      );
      const tableQs = tableQuery ? `?${tableQuery}` : '';

      history.push(getPath(expandTableRoute, { content: contentName }) + tableQs);
    },
    [content, queryParams.q, getPath, history, collapsedTablesRoute, expandTableRoute],
  );
}

export function useUpdatingSearchResultCounters(getThunk, resetAction) {
  const dispatch = useDispatch();
  const { q } = useQueryParams();

  useEffect(() => {
    if (!resetAction) return dispatch(getThunk(q));

    if (q?.length > 0) {
      dispatch(getThunk(q));
    } else {
      dispatch(resetAction());
    }

    return () => {
      if (resetAction) dispatch(resetAction());
    };
  }, [q, dispatch, getThunk, resetAction]);
}

export function useTryToRemindCheckPrimaryImageForGeneralTab({ itineraryItemName, neededLastAvailableStep }) {
  const itineraryItem = useSelector(itinerariesItemDataSelector);
  const pages = getPages(itineraryItem);
  const initialAreaPropertyPageIds = useMemo(() => getNeededPageIds(pages), [itineraryItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentAreaPropertyPageIds = useCallback((newSavedItineraryItem) => {
    return getNeededPageIds(getPages(newSavedItineraryItem));
  }, []);

  const { tryToRemindCheckPrimaryImages, checkPrimaryImageWarning } = useTryToRemindCheckPrimaryImage({
    neededLastAvailableStep,
    itineraryItemName,
    initialAreaPropertyPageIds,
    getCurrentAreaPropertyPageIds,
  });

  if (itineraryItem.detailsMode === EDetailsMode.Chronological) {
    return { onAfterSave: checkPrimaryImageWarning };
  }

  return { onAfterSave: tryToRemindCheckPrimaryImages };
}

function getNeededPageIds(pages = []) {
  const NEEDED_PAGE_TYPES = [PAGE_TYPE_PROPERTY, PAGE_TYPE_AREA, PAGE_TYPE_ACTIVITY];

  return pages.filter((p) => NEEDED_PAGE_TYPES.includes(p.type)).map((p) => p.id);
}

function getPages(itineraryItem) {
  const commonPages = itineraryItem.commonPages || [];
  const aboutSafariPages = itineraryItem.aboutSafariPages || [];
  const aboutUsPages = itineraryItem.aboutUsPages || [];
  const miscPages = itineraryItem.miscPages || [];

  return [...commonPages, ...aboutSafariPages, ...aboutUsPages, ...miscPages];
}

export function getDefaultFilter(filter = {}) {
  return {
    ...(filter || {}),
    data: {
      show: SEARCH_FILTER_SHOW_STATUS.ALL,
      tags: [],
    },
  };
}

export const useProposalDifferenceHandler = (pages) => {
  const [copyPages, setCopyPages] = useState([]);
  const [newPages, setNewPages] = useState([]);

  const updatedPages = difference(pages, copyPages);

  useEffect(() => {
    setCopyPages(clone(pages));
  }, [newPages]);

  return { setNewPages, updatedPages };
};

const addDataToBlock = (blocks, type, additionalData) => {
  return blocks.map((block) =>
    block.type === type ? { ...block, data: { ...block.data, ...additionalData } } : block,
  );
};

export const addDataToProposalBlocks = (proposal) => {
  const { additionalInfo, costs, guests, landing, scheduleType, schedules, specialOccasions, way, tags } = proposal;
  const landingPages = landing.pages.map((page) => {
    const { role, blocks } = page;

    switch (role) {
      case 'safari_costs':
        return { ...page, blocks: addDataToBlock(blocks, role, { ...costs }) };
      case 'safari_map':
        return { ...page, blocks: addDataToBlock(blocks, role, { scheduleType, way }) };
      case 'safari_overview':
        return { ...page, blocks: addDataToBlock(blocks, role, { schedules, scheduleType }) };
      case 'guest_forms':
        return { ...page, blocks: addDataToBlock(blocks, role, { additionalInfo, guests, specialOccasions }) };
      case 'image':
        return { ...page, blocks: addDataToBlock(blocks, role, { tags }) };
      default: {
        return page;
      }
    }
  });

  const newLanding = { ...landing, pages: landingPages };
  return { ...proposal, landing: newLanding };
};
